<template>
  <SafeTeleport to="#status-dropdown-target">
    <div>
      <Dropdown
        :items="statusOptions"
        :label="label"
        :disabled="isDisabled"
        @update:modelValue="handleStatusSelect"
      />
      <Modal
        v-if="modalOpen"
        title="Appointment Status Change"
        @hide-modal="hideModal"
        @submit="handleSave"
      >
        <div>
          <Alert type="warning" class="mb-3">
            <div>
              <p v-if="lostFields.length">
                By changing the status to "<span class="font-bold">{{
                  selectedStatus
                }}</span
                >" the following times will be reset.
              </p>
              <p v-else-if="requiredFields.length">
                By changing the status to "<span class="font-bold">{{
                  selectedStatus
                }}</span
                >" you will need to update the times below to reflect the
                correct time.
              </p>
              <template v-if="techImpact">
                <hr class="my-2 border-orange-200" />
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-orange-800/70 mb-1.5"
                >
                  Affected Technicians
                </p>
                <ul class="space-y-1 text-sm">
                  <li
                    v-for="item in techImpact.items"
                    :key="item.name"
                    class="flex items-baseline gap-1.5"
                  >
                    <span class="text-orange-400 text-[8px] leading-none mt-px"
                      >&#9679;</span
                    >
                    <span>
                      <span class="font-medium">{{ item.name }}</span>
                      <span
                        v-if="item.isPrimary"
                        class="text-xs text-gray-500 ml-0.5"
                        >(default)</span
                      >
                      <span class="text-gray-400 mx-0.5">&mdash;</span>
                      <span class="text-gray-600">{{ item.description }}</span>
                    </span>
                  </li>
                </ul>
                <p class="mt-2 text-xs leading-snug text-gray-500">
                  Each technician's status will update to match.<template
                    v-if="techImpact.hasCrewMembers"
                  >
                    Crew member times will sync with their assigned
                    technician.</template
                  >
                </p>
              </template>
            </div>
          </Alert>
          <AppointmentTimeline
            :appointment="appointment"
            :editable-times="requiredFields"
            :lost-times="lostFields"
            @update:modelValue="handleUpdateTime"
          />
          <TextArea
            v-model="reason"
            class="mt-4"
            message="Reason for status change"
          />
        </div>
      </Modal>
    </div>
  </SafeTeleport>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, ref } from "vue";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Dropdown from "@/components/Seranova/Dropdown/Dropdown.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import CONSTANTS from "@/Constants";
import { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";
const { APPOINTMENT_STATUS_ORDER, APPOINTMENT_STATUS_MAP } = CONSTANTS;
import { SafeTeleport } from "vue-safe-teleport";

import AppointmentTimeline from "./AppointmentTimeline.vue";
import { useAppointmentStatus } from "./useAppointmentStatus";

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const { getRequiredFieldsForStatus, times, changeStatus, getTimesToRemove } =
  useAppointmentStatus(props.appointment);
const selectedStatus = ref<AppointmentStatus | null>(null);
const modalOpen = ref(false);
const reason = ref("");
const links = computed(() => props.appointment.links || {});
const statusOptions = computed(() => {
  const availableStatuses = [props.appointment.status].concat(
    Object.keys(links.value)
  );
  // Sort statuses by flow
  const sortedStatuses = APPOINTMENT_STATUS_ORDER.filter((status) =>
    availableStatuses.includes(status)
  );
  return sortedStatuses
    .filter(
      (status) =>
        status !== props.appointment.status &&
        APPOINTMENT_STATUS_MAP[status] &&
        links.value[status]
    )
    .map((status) => ({
      value: status,
      label: APPOINTMENT_STATUS_MAP[status]
    }));
});
const isDisabled = computed(() => statusOptions.value.length === 0);
const requiredFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getRequiredFieldsForStatus(selectedStatus.value);
});
const lostFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getTimesToRemove(props.appointment.status, selectedStatus.value);
});

const label = computed(() => {
  const statusKey = props.appointment.status;
  return APPOINTMENT_STATUS_MAP[statusKey] || statusKey;
});

const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");

const TIME_FIELD_LABELS: Record<string, string> = {
  accepted_time: "Claimed",
  dispatch_time: "Travel Started",
  arrival_time: "Arrival",
  departure_time: "Departure"
};

const techImpact = computed(() => {
  if (!isMultiTechEnabled.value || !selectedStatus.value) return null;
  const ats = props.appointment.appointment_technicians || [];
  if (ats.length <= 1) return null;

  const techs = props.appointment.technicians || [];
  const techNameMap = new Map(techs.map((t) => [t.technician_id, t.full_name]));
  const hasCrewMembers = (props.appointment.crew_members || []).length > 0;
  const isDestructive = lostFields.value.length > 0;

  // Times being newly set (not already on the appointment)
  const fieldsBeingSet = requiredFields.value.filter(
    (f) => !props.appointment[f as keyof AppointmentV2]
  );

  const items = ats.map((at) => {
    const name =
      techNameMap.get(at.technician_profile_id) ||
      `Technician ${at.position + 1}`;
    const isPrimary = at.position === 0;

    if (isDestructive) {
      const clearedLabels = lostFields.value
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      return {
        name,
        isPrimary,
        description: `${clearedLabels} will be cleared`
      };
    }

    if (isPrimary) {
      return { name, isPrimary, description: "times will be updated directly" };
    }

    const preserved = fieldsBeingSet.filter((f) => at[f as keyof typeof at]);
    const willReceive = fieldsBeingSet.filter((f) => !at[f as keyof typeof at]);

    if (preserved.length > 0 && willReceive.length > 0) {
      const preservedLabels = preserved
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      const receiveLabels = willReceive
        .map((f) => TIME_FIELD_LABELS[f])
        .join(", ");
      return {
        name,
        isPrimary,
        description: `${receiveLabels} will be set; ${preservedLabels} already recorded individually`
      };
    }

    if (preserved.length > 0) {
      return {
        name,
        isPrimary,
        description: "all times already recorded individually — no changes"
      };
    }

    return { name, isPrimary, description: "times will be synced" };
  });

  return { items, isDestructive, hasCrewMembers };
});
const handleStatusSelect = (item: { value: string; label: string }) => {
  selectedStatus.value = item.value as AppointmentStatus;
  modalOpen.value = true;
};
const hideModal = () => {
  modalOpen.value = false;
};

const hasFutureTime = (): boolean => {
  return requiredFields.value.some(
    (field) => times[field] && moment(times[field]).isAfter(moment())
  );
};

const handleSave = async () => {
  if (!selectedStatus.value) return;
  const statusLink = links.value[selectedStatus.value];
  if (!statusLink) {
    window.notify.error("Unable to edit the status of this appointment.");
    return;
  }
  if (hasFutureTime()) {
    window.notify.error("Cannot use future time.");
    return;
  }
  await changeStatus(selectedStatus.value, times, reason.value, statusLink);
  modalOpen.value = false;
  window.notify.success("Appointment status updated");
  emit("refresh-appointment");
};
const handleUpdateTime = (data: { [k: string]: string }) => {
  const fieldName = Object.keys(data)[0];
  times[fieldName] = data[fieldName];
};
</script>

<style scoped></style>
